import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Dimensions,
  Image,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useTheme } from '../contexts/ThemeContext';
import { useNavigation } from '@react-navigation/native';

const { width } = Dimensions.get('window');

const IndexScreen = () => {
  const { colors } = useTheme();
  const navigation = useNavigation();
  const isMobile = width < 768;
  const isTablet = width >= 768 && width < 1024;

  // Stats data
  const stats = [
    { value: '+100', label: 'Clients Served' },
    { value: '50', label: 'ITRs Filed' },
    { value: '50', label: 'Business Registrations' },
    { value: '98%', label: 'Client Satisfaction' },
  ];

  // Services data
  const services = [
    {
      id: 'gst',
      title: 'GST Filing',
      description: 'Complete GST registration and timely returns filing to keep you business compliant and penalty-free.',
      icon: 'document-text',
      color: '#1a1a4e',
      screen: 'GSTFiling',
    },
    {
      id: 'itr',
      title: 'ITR Filing',
      description: 'File your Income Tax Returns accurately with expert guidance and maximize your tax benefits.',
      icon: 'calculator',
      color: '#1a1a4e',
      screen: 'ITRFiling',
    },
    {
      id: 'business',
      title: 'Business Registration',
      description: 'Start your business easily - Proprietorship, Partnership, LLP, or Pvt Ltd registration.',
      icon: 'briefcase',
      color: '#1a1a4e',
      screen: 'BusinessRegistration',
    },
    {
      id: 'rto',
      title: 'RTO Services',
      description: 'Fast and hassle-free RTO documentation including vehicle registration, transfers, and permits.',
      icon: 'car',
      color: '#1a1a4e',
      screen: 'RTOServices',
    },
  ];

  const styles = StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: '#f8fafc',
    },
    scrollView: {
      flex: 1,
    },

    // Hero Section - Enhanced
    heroSection: {
      backgroundColor: '#1a1a4e',
      paddingTop: 80,
      paddingBottom: 140,
      paddingHorizontal: 20,
      position: 'relative',
      overflow: 'hidden',
    },
    heroGradientTop: {
      display: 'none', // Hidden for single color background
    },
    heroDecorCircle1: {
      display: 'none', // Hidden for single color background
    },
    heroDecorCircle2: {
      display: 'none', // Hidden for single color background
    },
    heroDecorLine: {
      position: 'absolute',
      top: '50%',
      left: 0,
      right: 0,
      height: 1,
      backgroundColor: 'rgba(201, 164, 78, 0.1)',
    },
    heroContent: {
      maxWidth: 900,
      alignSelf: 'center',
      alignItems: 'center',
      zIndex: 1,
    },
    heroBadge: {
      backgroundColor: 'rgba(201, 164, 78, 0.15)',
      borderWidth: 1,
      borderColor: 'rgba(201, 164, 78, 0.3)',
      paddingVertical: 8,
      paddingHorizontal: 20,
      borderRadius: 50,
      marginBottom: 24,
    },
    heroBadgeText: {
      color: '#c9a44e',
      fontSize: 12,
      fontWeight: '700',
      textTransform: 'uppercase',
      letterSpacing: 2,
    },
    heroTitle: {
      fontSize: isMobile ? 32 : isTablet ? 42 : 56,
      fontWeight: '800',
      color: '#fff',
      textAlign: 'center',
      marginBottom: 8,
      lineHeight: isMobile ? 40 : isTablet ? 52 : 68,
    },
    heroTitleAccent: {
      color: '#c9a44e',
    },
    heroSubtitle: {
      fontSize: isMobile ? 15 : 18,
      color: 'rgba(255, 255, 255, 0.75)',
      textAlign: 'center',
      maxWidth: 650,
      lineHeight: isMobile ? 24 : 28,
      marginBottom: 36,
    },
    heroButtonsRow: {
      flexDirection: isMobile ? 'column' : 'row',
      gap: 16,
      alignItems: 'center',
    },
    heroButton: {
      backgroundColor: '#c9a44e',
      paddingVertical: 16,
      paddingHorizontal: 32,
      borderRadius: 8,
      flexDirection: 'row',
      alignItems: 'center',
      gap: 10,
      shadowColor: '#c9a44e',
      shadowOffset: { width: 0, height: 8 },
      shadowOpacity: 0.3,
      shadowRadius: 16,
      elevation: 8,
    },
    heroButtonText: {
      color: '#1a1a4e',
      fontWeight: '700',
      fontSize: 16,
    },
    heroButtonSecondary: {
      backgroundColor: 'transparent',
      borderWidth: 2,
      borderColor: 'rgba(255, 255, 255, 0.3)',
      paddingVertical: 14,
      paddingHorizontal: 28,
      borderRadius: 8,
      flexDirection: 'row',
      alignItems: 'center',
      gap: 10,
    },
    heroButtonSecondaryText: {
      color: '#fff',
      fontWeight: '600',
      fontSize: 15,
    },

    // Stats Section
    statsSection: {
      marginTop: -60,
      paddingHorizontal: 20,
      marginBottom: 60,
    },
    statsContainer: {
      backgroundColor: '#fff',
      borderRadius: 16,
      paddingVertical: 30,
      paddingHorizontal: 20,
      maxWidth: 900,
      alignSelf: 'center',
      width: '100%',
      flexDirection: isMobile ? 'column' : 'row',
      justifyContent: 'space-around',
      alignItems: 'center',
      gap: isMobile ? 24 : 0,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 8 },
      shadowOpacity: 0.1,
      shadowRadius: 24,
      elevation: 12,
    },
    statItem: {
      alignItems: 'center',
      paddingHorizontal: 20,
    },
    statValue: {
      fontSize: isMobile ? 32 : 40,
      fontWeight: '800',
      color: '#1a1a4e',
      marginBottom: 6,
    },
    statLabel: {
      fontSize: 14,
      color: '#6b7280',
      textAlign: 'center',
    },
    statDivider: {
      width: 1,
      height: 50,
      backgroundColor: '#e5e7eb',
    },

    // Why Choose Section
    whyChooseSection: {
      paddingVertical: 60,
      paddingHorizontal: 20,
      backgroundColor: '#fff',
    },
    whyChooseContainer: {
      maxWidth: 1100,
      alignSelf: 'center',
      width: '100%',
      flexDirection: isMobile ? 'column' : 'row',
      gap: 40,
    },
    whyChooseLeft: {
      flex: isMobile ? undefined : 0.4,
      marginBottom: isMobile ? 20 : 0,
    },
    whyChooseLabel: {
      fontSize: 13,
      color: '#c9a44e',
      fontWeight: '600',
      textTransform: 'uppercase',
      letterSpacing: 1,
      marginBottom: 10,
    },
    whyChooseTitle: {
      fontSize: isMobile ? 28 : 36,
      fontWeight: '800',
      color: '#1a1a4e',
      lineHeight: isMobile ? 36 : 46,
    },
    whyChooseRight: {
      flex: isMobile ? undefined : 0.6,
      flexDirection: isMobile ? 'column' : 'row',
      gap: 30,
    },
    whyChooseColumn: {
      flex: 1,
    },
    whyChooseText: {
      fontSize: 15,
      color: '#4b5563',
      lineHeight: 26,
      marginBottom: 16,
    },

    // Services Section
    servicesSection: {
      paddingVertical: 60,
      paddingHorizontal: 20,
      backgroundColor: '#f8fafc',
    },
    servicesContainer: {
      maxWidth: 1100,
      alignSelf: 'center',
      width: '100%',
      flexDirection: isMobile ? 'column' : 'row',
      gap: 40,
    },
    servicesLeft: {
      flex: isMobile ? undefined : 0.4,
      marginBottom: isMobile ? 30 : 0,
    },
    servicesLabel: {
      fontSize: 13,
      color: '#c9a44e',
      fontWeight: '600',
      textTransform: 'uppercase',
      letterSpacing: 1,
      marginBottom: 10,
    },
    servicesTitle: {
      fontSize: isMobile ? 28 : 36,
      fontWeight: '800',
      color: '#1a1a4e',
      lineHeight: isMobile ? 36 : 46,
      marginBottom: 20,
    },
    servicesDescription: {
      fontSize: 15,
      color: '#6b7280',
      lineHeight: 26,
      marginBottom: 24,
    },
    practiceAreasButton: {
      backgroundColor: '#c9a44e',
      paddingVertical: 14,
      paddingHorizontal: 24,
      borderRadius: 6,
      alignSelf: 'flex-start',
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
    },
    practiceAreasButtonText: {
      color: '#1a1a4e',
      fontWeight: '700',
      fontSize: 14,
    },
    servicesRight: {
      flex: isMobile ? undefined : 0.6,
    },
    servicesGrid: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 16,
    },
    serviceCard: {
      backgroundColor: '#1a1a4e',
      borderRadius: 12,
      padding: 24,
      width: isMobile ? '100%' : 'calc(50% - 8px)',
      minWidth: isMobile ? undefined : 240,
      flex: isMobile ? undefined : 1,
      maxWidth: isMobile ? undefined : '48%',
    },
    serviceCardIcon: {
      width: 44,
      height: 44,
      borderRadius: 22,
      backgroundColor: 'rgba(201, 164, 78, 0.2)',
      justifyContent: 'center',
      alignItems: 'center',
      marginBottom: 16,
    },
    serviceCardTitle: {
      fontSize: 18,
      fontWeight: '700',
      color: '#c9a44e',
      marginBottom: 10,
    },
    serviceCardDescription: {
      fontSize: 13,
      color: 'rgba(255, 255, 255, 0.75)',
      lineHeight: 20,
    },

    // Quick Links Section
    quickLinksSection: {
      paddingVertical: 60,
      paddingHorizontal: 20,
      backgroundColor: '#fff',
    },
    quickLinksContainer: {
      maxWidth: 1100,
      alignSelf: 'center',
      width: '100%',
      alignItems: 'center',
    },
    quickLinksTitle: {
      fontSize: isMobile ? 24 : 32,
      fontWeight: '800',
      color: '#1a1a4e',
      marginBottom: 12,
      textAlign: 'center',
    },
    quickLinksSubtitle: {
      fontSize: 15,
      color: '#6b7280',
      marginBottom: 30,
      textAlign: 'center',
    },
    quickLinksGrid: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      justifyContent: 'center',
      gap: 16,
    },
    quickLinkCard: {
      backgroundColor: '#f8fafc',
      borderRadius: 12,
      padding: 20,
      width: isMobile ? '100%' : isTablet ? '45%' : '22%',
      alignItems: 'center',
      borderWidth: 1,
      borderColor: '#e5e7eb',
    },
    quickLinkIcon: {
      width: 56,
      height: 56,
      borderRadius: 28,
      backgroundColor: '#1a1a4e',
      justifyContent: 'center',
      alignItems: 'center',
      marginBottom: 12,
    },
    quickLinkTitle: {
      fontSize: 16,
      fontWeight: '700',
      color: '#1a1a4e',
      marginBottom: 6,
      textAlign: 'center',
    },
    quickLinkDescription: {
      fontSize: 13,
      color: '#6b7280',
      textAlign: 'center',
    },

    // Testimonials Section
    testimonialsSection: {
      paddingVertical: 60,
      paddingHorizontal: 20,
      backgroundColor: '#f8fafc',
    },
    testimonialsContainer: {
      maxWidth: 1100,
      alignSelf: 'center',
      width: '100%',
    },
    testimonialsTitle: {
      fontSize: isMobile ? 24 : 32,
      fontWeight: '800',
      color: '#1a1a4e',
      textAlign: 'center',
      marginBottom: 12,
    },
    testimonialsSubtitle: {
      fontSize: 15,
      color: '#6b7280',
      textAlign: 'center',
      marginBottom: 40,
    },
    testimonialsGrid: {
      flexDirection: isMobile ? 'column' : 'row',
      gap: 24,
    },
    testimonialCard: {
      flex: 1,
      backgroundColor: '#fff',
      borderRadius: 12,
      padding: 24,
      borderWidth: 1,
      borderColor: '#e5e7eb',
    },
    testimonialStars: {
      flexDirection: 'row',
      marginBottom: 16,
    },
    testimonialText: {
      fontSize: 15,
      color: '#374151',
      lineHeight: 24,
      marginBottom: 20,
      fontStyle: 'italic',
    },
    testimonialAuthor: {
      fontSize: 16,
      fontWeight: '700',
      color: '#1a1a4e',
    },
    testimonialRole: {
      fontSize: 13,
      color: '#6b7280',
    },

    // CTA Section
    ctaSection: {
      backgroundColor: '#1a1a4e',
      paddingVertical: 60,
      paddingHorizontal: 20,
    },
    ctaContainer: {
      maxWidth: 800,
      alignSelf: 'center',
      alignItems: 'center',
    },
    ctaTitle: {
      fontSize: isMobile ? 24 : 32,
      fontWeight: '800',
      color: '#c9a44e',
      textAlign: 'center',
      marginBottom: 16,
    },
    ctaText: {
      fontSize: 16,
      color: 'rgba(255, 255, 255, 0.8)',
      textAlign: 'center',
      marginBottom: 30,
      maxWidth: 600,
    },
    ctaButtonsRow: {
      flexDirection: isMobile ? 'column' : 'row',
      gap: 16,
    },
    ctaPrimaryButton: {
      backgroundColor: '#c9a44e',
      paddingVertical: 16,
      paddingHorizontal: 32,
      borderRadius: 8,
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
    },
    ctaPrimaryButtonText: {
      color: '#1a1a4e',
      fontWeight: '700',
      fontSize: 16,
    },
    ctaSecondaryButton: {
      backgroundColor: 'transparent',
      paddingVertical: 16,
      paddingHorizontal: 32,
      borderRadius: 8,
      borderWidth: 2,
      borderColor: '#c9a44e',
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
    },
    ctaSecondaryButtonText: {
      color: '#c9a44e',
      fontWeight: '700',
      fontSize: 16,
    },
  });

  return (
    <View style={styles.container}>
      <Header />
      <ScrollView style={styles.scrollView} showsVerticalScrollIndicator={false}>
        {/* Hero Section */}
        <View style={styles.heroSection}>
          {/* Decorative Elements */}
          <View style={styles.heroGradientTop} />
          <View style={styles.heroDecorCircle1} />
          <View style={styles.heroDecorCircle2} />

          <View style={styles.heroContent}>
            <View style={styles.heroBadge}>
              <Text style={styles.heroBadgeText}>Trusted Accounting Partner</Text>
            </View>
            <Text style={styles.heroTitle}>
              We Handle Your{'\n'}<Text style={styles.heroTitleAccent}>Finances With Precision</Text>
            </Text>
            <Text style={styles.heroSubtitle}>
              From GST filing to ITR, RTO services, and business registrations — we ensure every financial and legal process is done accurately and on time. Let Modern Munshiji simplify your accounting journey.
            </Text>
            <View style={styles.heroButtonsRow}>
              <TouchableOpacity
                style={styles.heroButton}
                onPress={() => navigation.navigate('GSTFiling')}
              >
                <Ionicons name="rocket" size={20} color="#1a1a4e" />
                <Text style={styles.heroButtonText}>Get Started</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.heroButtonSecondary}
                onPress={() => navigation.navigate('CalculatorsHome')}
              >
                <Ionicons name="calculator" size={18} color="#fff" />
                <Text style={styles.heroButtonSecondaryText}>Explore Tools</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>

        {/* Stats Section */}
        <View style={styles.statsSection}>
          <View style={styles.statsContainer}>
            {stats.map((stat, index) => (
              <React.Fragment key={stat.label}>
                <View style={styles.statItem}>
                  <Text style={styles.statValue}>{stat.value}</Text>
                  <Text style={styles.statLabel}>{stat.label}</Text>
                </View>
                {!isMobile && index < stats.length - 1 && (
                  <View style={styles.statDivider} />
                )}
              </React.Fragment>
            ))}
          </View>
        </View>

        {/* Why Choose Modern Munshiji Section */}
        <View style={styles.whyChooseSection}>
          <View style={styles.whyChooseContainer}>
            <View style={styles.whyChooseLeft}>
              <Text style={styles.whyChooseLabel}>One Step Ahead</Text>
              <Text style={styles.whyChooseTitle}>
                Why Choose{'\n'}Modern{'\n'}Munshiji
              </Text>
            </View>
            <View style={styles.whyChooseRight}>
              <View style={styles.whyChooseColumn}>
                <Text style={styles.whyChooseText}>
                  At Modern Munshiji, we combine technology with trusted expertise to make accounting simple, transparent, and stress-free. Our team of professionals delivers timely filings,
                </Text>
              </View>
              <View style={styles.whyChooseColumn}>
                <Text style={styles.whyChooseText}>
                  accurate documentation, and complete compliance — helping individuals and businesses stay financially sound without the paperwork hassle.
                </Text>
              </View>
            </View>
          </View>
        </View>

        {/* Services Section */}
        <View style={styles.servicesSection}>
          <View style={styles.servicesContainer}>
            <View style={styles.servicesLeft}>
              <Text style={styles.servicesLabel}>360 Legal Solutions</Text>
              <Text style={styles.servicesTitle}>
                Explore What We{'\n'}Are Doing Better
              </Text>
              <Text style={styles.servicesDescription}>
                We cover every aspect of accounting and legal documentation — so you can focus on your goals while we handle the rest.
              </Text>
              <TouchableOpacity
                style={styles.practiceAreasButton}
                onPress={() => navigation.navigate('ToolsHome')}
              >
                <Text style={styles.practiceAreasButtonText}>Practice Areas</Text>
                <Ionicons name="arrow-forward" size={16} color="#1a1a4e" />
              </TouchableOpacity>
            </View>
            <View style={styles.servicesRight}>
              <View style={styles.servicesGrid}>
                {services.map((service) => (
                  <TouchableOpacity
                    key={service.id}
                    style={styles.serviceCard}
                    onPress={() => navigation.navigate(service.screen)}
                    activeOpacity={0.8}
                  >
                    <View style={styles.serviceCardIcon}>
                      <Ionicons name={service.icon} size={22} color="#c9a44e" />
                    </View>
                    <Text style={styles.serviceCardTitle}>{service.title}</Text>
                    <Text style={styles.serviceCardDescription}>
                      {service.description}
                    </Text>
                    <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 12 }}>
                      <Text style={{ color: '#c9a44e', fontSize: 13, fontWeight: '600' }}>Get Quote</Text>
                      <Ionicons name="arrow-forward" size={14} color="#c9a44e" style={{ marginLeft: 6 }} />
                    </View>
                  </TouchableOpacity>
                ))}
              </View>
            </View>
          </View>
        </View>

        {/* Quick Links Section */}
        <View style={styles.quickLinksSection}>
          <View style={styles.quickLinksContainer}>
            <Text style={styles.quickLinksTitle}>Explore Our Tools</Text>
            <Text style={styles.quickLinksSubtitle}>
              Powerful financial calculators and business tools at your fingertips
            </Text>
            <View style={styles.quickLinksGrid}>
              <TouchableOpacity
                style={styles.quickLinkCard}
                onPress={() => navigation.navigate('CalculatorsHome')}
              >
                <View style={styles.quickLinkIcon}>
                  <Ionicons name="calculator" size={24} color="#c9a44e" />
                </View>
                <Text style={styles.quickLinkTitle}>Calculators</Text>
                <Text style={styles.quickLinkDescription}>
                  Tax, EMI, SIP & more
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.quickLinkCard}
                onPress={() => navigation.navigate('InvoiceGenerator')}
              >
                <View style={styles.quickLinkIcon}>
                  <Ionicons name="receipt" size={24} color="#c9a44e" />
                </View>
                <Text style={styles.quickLinkTitle}>Invoice Generator</Text>
                <Text style={styles.quickLinkDescription}>
                  Create GST invoices
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.quickLinkCard}
                onPress={() => navigation.navigate('QuotationMaker')}
              >
                <View style={styles.quickLinkIcon}>
                  <Ionicons name="document-text" size={24} color="#c9a44e" />
                </View>
                <Text style={styles.quickLinkTitle}>Quotation Maker</Text>
                <Text style={styles.quickLinkDescription}>
                  Professional quotes
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.quickLinkCard}
                onPress={() => navigation.navigate('IncomeTaxCalculator')}
              >
                <View style={styles.quickLinkIcon}>
                  <Ionicons name="pie-chart" size={24} color="#c9a44e" />
                </View>
                <Text style={styles.quickLinkTitle}>Income Tax</Text>
                <Text style={styles.quickLinkDescription}>
                  Calculate your taxes
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>

        {/* Testimonials Section */}
        <View style={styles.testimonialsSection}>
          <View style={styles.testimonialsContainer}>
            <Text style={styles.testimonialsTitle}>What Our Clients Say</Text>
            <Text style={styles.testimonialsSubtitle}>
              Trusted by hundreds of businesses and individuals across India
            </Text>
            <View style={styles.testimonialsGrid}>
              <View style={styles.testimonialCard}>
                <View style={styles.testimonialStars}>
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Ionicons key={star} name="star" size={18} color="#c9a44e" />
                  ))}
                </View>
                <Text style={styles.testimonialText}>
                  "Modern Munshiji made our GST filing completely stress-free. Their team is professional and always available to answer queries. Highly recommended!"
                </Text>
                <Text style={styles.testimonialAuthor}>Rajesh Sharma</Text>
                <Text style={styles.testimonialRole}>Small Business Owner</Text>
              </View>

              <View style={styles.testimonialCard}>
                <View style={styles.testimonialStars}>
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Ionicons key={star} name="star" size={18} color="#c9a44e" />
                  ))}
                </View>
                <Text style={styles.testimonialText}>
                  "Best ITR filing service! They helped me claim refunds I didn't even know I was eligible for. Their calculators are also very useful."
                </Text>
                <Text style={styles.testimonialAuthor}>Priya Patel</Text>
                <Text style={styles.testimonialRole}>IT Professional</Text>
              </View>

              <View style={styles.testimonialCard}>
                <View style={styles.testimonialStars}>
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Ionicons key={star} name="star" size={18} color="#c9a44e" />
                  ))}
                </View>
                <Text style={styles.testimonialText}>
                  "Registered my Pvt Ltd company through them. The process was smooth and transparent. They handled all the paperwork efficiently."
                </Text>
                <Text style={styles.testimonialAuthor}>Amit Kumar</Text>
                <Text style={styles.testimonialRole}>Startup Founder</Text>
              </View>
            </View>
          </View>
        </View>

        {/* How It Works Section */}
        <View style={{ backgroundColor: '#fff', paddingVertical: 60, paddingHorizontal: 20 }}>
          <View style={{ maxWidth: 1100, alignSelf: 'center', width: '100%' }}>
            <Text style={{ fontSize: isMobile ? 24 : 32, fontWeight: '800', color: '#1a1a4e', textAlign: 'center', marginBottom: 12 }}>
              How Modern Munshiji Works
            </Text>
            <Text style={{ fontSize: 15, color: '#6b7280', textAlign: 'center', marginBottom: 40, maxWidth: 600, alignSelf: 'center' }}>
              Simple, transparent, and hassle-free process to handle all your financial and legal needs
            </Text>
            <View style={{ flexDirection: isMobile ? 'column' : 'row', gap: 24 }}>
              {[
                { step: '01', icon: 'call', title: 'Get in Touch', desc: 'Contact us via phone, email, or through our website form. Tell us about your requirements.' },
                { step: '02', icon: 'document-text', title: 'Share Documents', desc: 'Submit the required documents securely. We guide you on exactly what\'s needed.' },
                { step: '03', icon: 'settings', title: 'We Process', desc: 'Our expert team handles all filings, registrations, and compliance requirements.' },
                { step: '04', icon: 'checkmark-circle', title: 'Done!', desc: 'Receive your completed filings, certificates, and acknowledgments on time.' },
              ].map((item, index) => (
                <View key={index} style={{ flex: 1, alignItems: 'center', position: 'relative' }}>
                  <View style={{ width: 60, height: 60, borderRadius: 30, backgroundColor: '#1a1a4e', justifyContent: 'center', alignItems: 'center', marginBottom: 16 }}>
                    <Ionicons name={item.icon} size={28} color="#c9a44e" />
                  </View>
                  <Text style={{ fontSize: 12, color: '#c9a44e', fontWeight: '700', marginBottom: 8 }}>STEP {item.step}</Text>
                  <Text style={{ fontSize: 18, fontWeight: '700', color: '#1a1a4e', marginBottom: 8, textAlign: 'center' }}>{item.title}</Text>
                  <Text style={{ fontSize: 14, color: '#6b7280', textAlign: 'center', lineHeight: 22 }}>{item.desc}</Text>
                </View>
              ))}
            </View>
          </View>
        </View>

        {/* CTA Section */}
        <View style={styles.ctaSection}>
          <View style={styles.ctaContainer}>
            <Text style={styles.ctaTitle}>Ready to Simplify Your Finances?</Text>
            <Text style={styles.ctaText}>
              Get started with Modern Munshiji today. Our experts are ready to help you with GST, ITR, business registration, and more.
            </Text>
            <View style={styles.ctaButtonsRow}>
              <TouchableOpacity
                style={styles.ctaPrimaryButton}
                onPress={() => navigation.navigate('Contact')}
              >
                <Ionicons name="call" size={18} color="#1a1a4e" />
                <Text style={styles.ctaPrimaryButtonText}>Contact Us</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.ctaSecondaryButton}
                onPress={() => navigation.navigate('CalculatorsHome')}
              >
                <Ionicons name="calculator" size={18} color="#c9a44e" />
                <Text style={styles.ctaSecondaryButtonText}>Explore Calculators</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>

        <Footer />
      </ScrollView >
    </View >
  );
};

export default IndexScreen;